import type { UserCreateSchema } from '@repo/schemas/user/userCreate';
import type { selectedRowsI } from '@repo/types/table';

import { usePostUser } from '@repo/api/user/usePostUser';
import { cn } from '@repo/lib/cn';
import { ErrorMessageHandler } from '@repo/lib/errorMessageHandler';
import { generatePassword } from '@repo/lib/generatePassword';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { AxiosError, isAxiosError } from 'axios';
import { CheckCircle2Icon, CheckIcon, LoaderCircleIcon, XIcon } from 'lucide-react';
import { useEffect, useState } from 'react';

import type { UsersMassImportRow } from '@/pages/_main/manage/user/ManageUsersMassImportPage';

import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { LoadingSwap } from '@/components/ui/loading-swap';

/**
 * Props for the UserMassImport component.
 * @property {UsersMassImportRow[]} data - Array of user data to import.
 * @property {boolean} open - Whether the dialog is open.
 * @property {(value: boolean) => void} onOpenChange - Callback to change dialog open state.
 */
interface UserMassImportProps {
  data: UsersMassImportRow[];
  open: boolean;
  onOpenChange: (value: boolean) => void;
}

export default function UserMassImport(userMassImportProps: UserMassImportProps) {
  const { data, open, onOpenChange } = userMassImportProps;
  const { mutateAsync, isPending } = usePostUser();
  const [isFinished, setIsFinished] = useState<boolean>(false);

  const [selectedRows, setSelectedRows] = useState<selectedRowsI<UserCreateSchema>[]>([]);
  const [, setSuccessRows] = useState<selectedRowsI<UserCreateSchema>[]>([]);

  const getErrorMessage = (error?: Error | AxiosError | null) => {
    const errorHandler = new ErrorMessageHandler(baseErrorMessages);
    const errorObj = errorHandler.processError(error);
    if (isAxiosError(error) && error?.response?.data?.path === 'email') {
      return 'Użytkownik o podanym emailu już istnieje';
    }

    return errorObj.detail;
  };

  useEffect(() => {
    setSelectedRows(
      data.map((row) => {
        const userEntity: UserCreateSchema = {
          ...row,
          lvl: Number(row.lvl),
          birthdate: row.birthdate,
          password: generatePassword(12),
        };
        return { data: userEntity, status: 'isIdle' };
      })
    );
    setIsFinished(false);
  }, [data]);

  const onClose = () => {
    onOpenChange(false);
    setIsFinished(false);
  };

  const onSubmit = async () => {
    const successfullyAddedUsers: selectedRowsI<UserCreateSchema>[] = [];

    selectedRows.forEach((row) => {
      const rowIndex = selectedRows.findIndex((r) => r.data === row.data);

      // Set the status of the current row to 'isLoading'
      setSelectedRows((prevRows) => {
        const newRows = [...prevRows];
        if (newRows[rowIndex]) {
          newRows[rowIndex].status = 'isPending';
        }
        return newRows;
      });

      mutateAsync(row.data)
        .then(() => {
          successfullyAddedUsers.push({ ...row, status: 'isSuccess' });

          // If the request is successful, set the status of the current row to 'isSuccess'
          setSelectedRows((prevRows) => {
            const newRows = [...prevRows];
            if (newRows[rowIndex]) {
              newRows[rowIndex].status = 'isSuccess';
            }
            return newRows;
          });
        })
        .catch((error: AxiosError) => {
          // If the request fails, set the status of the current row to 'isError'
          setSelectedRows((prevRows) => {
            const newRows = [...prevRows];
            if (newRows[rowIndex]) {
              newRows[rowIndex].status = 'isError';
              newRows[rowIndex].error = error;
            }

            return newRows;
          });
        });
    });

    setIsFinished(true);
    setSuccessRows(successfullyAddedUsers);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={'md:max-w-lg'}>
        <DialogBody>
          <DialogHeader className="bg-card flex flex-col border-b">
            <DialogTitle>Dodaj użytkowników</DialogTitle>
            <DialogDescription>
              Kliknięcie przycisku „Zapisz” spowoduje dodanie użytkowników oraz wysłanie do nich wiadomości e-mail z informacją o utworzeniu kont. Upewnij się, że wszystkie dane są poprawne i
              prawidłowe.
            </DialogDescription>
          </DialogHeader>
          <div className={'spacee-y-1 divide-y overflow-auto'}>
            {selectedRows.map(({ data, status, error }, index) => {
              const detail: string = getErrorMessage(error);
              return (
                <div key={index} className="py-2">
                  <div
                    className={cn('justify-start gap-3 p-2 leading-6 hover:cursor-pointer hover:no-underline', {
                      'text-text-destructive': status === 'isError',
                      'text-text-success': status === 'isSuccess',
                    })}
                  >
                    <div className="flex items-center gap-4">
                      <div className={'size-4'}>
                        {status === 'isError' && <XIcon className={'size-4 shrink-0'} />}
                        {status === 'isPending' && <LoaderCircleIcon className={'size-4 shrink-0 animate-spin'} />}
                        {status === 'isSuccess' && <CheckIcon className={'size-4 shrink-0'} />}
                      </div>
                      <p className={'text-muted-foreground w-3 text-sm text-nowrap'}>{index + 1}.</p>
                      <div className={'space-y-0.5'}>
                        <p className={'text-sm'}>
                          {data.name} {data.surname}
                        </p>
                        <p className={'text-muted-foreground text-xs'}>{data.email}</p>
                      </div>
                    </div>
                  </div>
                  <div className="text-muted-foreground ps-7 pb-2 text-xs empty:hidden">
                    {status === 'isError' && `Błąd: ${detail}`}
                    {status === 'isSuccess' && `Mail aktywacyjny zostanie wysłany na ${data.email}`}
                  </div>
                </div>
              );
            })}
          </div>
        </DialogBody>
        <DialogFooter className="bg-accent border-t">
          {isFinished ? (
            <Button variant={'tertiary'} onClick={onClose}>
              Zamknij
            </Button>
          ) : (
            <>
              <Button variant={'tertiary'} disabled={isPending} onClick={onClose}>
                Anuluj
              </Button>
              <Button type={'button'} onClick={onSubmit} disabled={isPending}>
                <LoadingSwap isLoading={isPending} className="flex items-center gap-2">
                  <CheckCircle2Icon />
                  Zapisz
                </LoadingSwap>
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
